import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "@/services/api";
import TodoModal from "./todoModal";
import { useSelector } from "react-redux";
import { BsCheckCircleFill, BsCircle, BsExclamationCircleFill, BsInfoCircleFill, BsThreeDotsVertical, BsBookFill } from "react-icons/bs";

export default ({ answer, setAnswer }) => {
  const [todos, setTodos] = useState([]);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    loadTodos();
  }, []);

  const loadTodos = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/mission_ao_todo/search", { search: "", page: 1, pageSize: 100, mission_ao_answer_id: answer._id });
      if (!ok) throw new Error("Failed to load todos");

      // filter completed at last, need info middle, pending first
      const sortedTodos = data.sort((a, b) => {
        // Helper function to get priority value
        const getPriority = (status) => {
          switch (status) {
            case "pending":
              return 0;
            case "need_info":
              return 1;
            case "completed":
              return 2;
            default:
              return 3;
          }
        };
        // Sort by status priority
        const priorityA = getPriority(a.status);
        const priorityB = getPriority(b.status);
        return priorityA - priorityB;
      });

      setTodos(sortedTodos);
    } catch (error) {
      console.error(error);
      toast.error("Error loading todos");
      setTodos([]);
    } finally {
      setLoading(false);
    }
  };

  const addTodo = async () => {
    try {
      const newTodo = {
        mission_ao_answer_id: answer._id,
        title: "",
        lead_user_id: user?._id || "",
        lead_user_name: user?.name || "",
        lead_user_avatar: user?.avatar || "",
        date: new Date().toISOString(),
        status: "pending",
        documents: [],
        comments: [],
      };

      const { ok, data } = await api.post("/mission_ao_todo", newTodo);
      if (!ok) throw new Error("Failed to create todo");

      setTodos([data, ...todos]);
      setSelectedTodo(data);
      setShowModal(true);
      toast.success("Todo created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error creating todo");
    }
  };

  const updateTodo = async (todoToUpdate) => {
    try {
      if (!todoToUpdate?._id) return;

      const { ok, data } = await api.put(`/mission_ao_todo/${todoToUpdate._id}`, todoToUpdate);
      if (!ok) throw new Error("Failed to update todo");

      setTodos(todos.map((t) => (t._id === data?._id ? data : t)));
      toast.success("Todo updated successfully!");
      return data;
    } catch (error) {
      console.error(error);
      toast.error("Error updating todo");
      return null;
    }
  };

  const updateField = async (id, field, value) => {
    const updatedTodo = { ...selectedTodo, [field]: value };
    setSelectedTodo(updatedTodo);
    const result = await updateTodo(updatedTodo);
    if (!result) {
      setSelectedTodo((prev) => ({ ...prev })); // Restore previous state if update failed
    }
  };

  const deleteTodo = async (id) => {
    try {
      const { ok } = await api.remove(`/mission_ao_todo/${id}`);
      if (!ok) throw new Error("Failed to delete todo");

      setTodos(todos.filter((t) => t._id !== id));
      setShowModal(false);
      toast.success("Todo deleted successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error deleting todo");
    }
  };

  const addComment = async (todoId, comment) => {
    try {
      const { ok, data } = await api.post(`/mission_ao_todo/${todoId}/comment`, {
        text: comment,
        user_id: user._id,
        user_name: user.name,
        user_avatar: user.avatar,
      });
      if (!ok) throw new Error("Failed to add comment");

      setTodos(todos.map((t) => (t._id === data?._id ? data : t)));
      setSelectedTodo(data);
      toast.success("Comment added successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error adding comment");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <BsCheckCircleFill className="text-green-500 w-5 h-5" />;
      case "read_again":
        return <BsBookFill className="text-yellow-500 w-5 h-5" />;
      case "need_info":
        return <BsExclamationCircleFill className="text-orange-500 w-5 h-5" />;
      case "in_progress":
        return <BsInfoCircleFill className="text-blue-500 w-5 h-5" />;
      default:
        return <BsCircle className="text-gray-300 w-5 h-5" />;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      completed: { bg: "bg-green-100", text: "text-green-800", label: "Terminé" },
      need_info: { bg: "bg-orange-100", text: "text-orange-800", label: "Besoin d'info" },
      in_progress: { bg: "bg-blue-100", text: "text-blue-800", label: "En cours" },
      read_again: { bg: "bg-yellow-100", text: "text-yellow-800", label: "À relire" },
      pending: { bg: "bg-gray-100", text: "text-gray-800", label: "En attente" },
    };

    const { bg, text, label } = statusMap[status] || statusMap.pending;

    return <span className={`${bg} ${text} px-3 py-1 rounded-full text-xs font-medium`}>{label}</span>;
  };

  // Filter todos based on selected filter
  const filteredTodos = filter === "all" ? todos : todos.filter((todo) => todo.status === filter);

  // Group by date
  const todosByDate = filteredTodos.reduce((acc, todo) => {
    const date = todo.date ? new Date(todo.date).toLocaleDateString() : "Sans date";
    if (!acc[date]) acc[date] = [];
    acc[date].push(todo);
    return acc;
  }, {});

  const todoCount = todos.length;
  const completedCount = todos.filter((t) => t.status === "completed").length;
  const pendingCount = todos.filter((t) => t.status !== "completed").length;
  const progressPercentage = todoCount ? Math.round((completedCount / todoCount) * 100) : 0;

  return (
    <div className="p-6">
      {/* Progress and Filter Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between mb-4">
          <div className="flex flex-col mb-4 md:mb-0">
            <h2 className="text-lg font-semibold text-gray-900">{answer.title}</h2>
            <p className="text-sm text-gray-500">Gestion des tâches liées à votre réponse d'appel d'offres</p>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-500">Avancement:</span>
              <span className="text-sm font-semibold">{progressPercentage}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-1">
              <div className="h-full bg-green-500 rounded-full" style={{ width: `${progressPercentage}%` }}></div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === "all" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}>
            Toutes ({todos.length})
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === "pending" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}>
            En attente ({todos.filter((t) => t.status === "pending").length})
          </button>
          <button
            onClick={() => setFilter("in_progress")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === "in_progress" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}>
            En cours ({todos.filter((t) => t.status === "in_progress").length})
          </button>
          <button
            onClick={() => setFilter("need_info")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === "need_info" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}>
            Info requise ({todos.filter((t) => t.status === "need_info").length})
          </button>
          <button
            onClick={() => setFilter("read_again")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === "read_again" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}>
            À relire ({todos.filter((t) => t.status === "read_again").length})
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === "completed" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}>
            Terminées ({todos.filter((t) => t.status === "completed").length})
          </button>
        </div>
      </div>

      {/* Todo Lists */}
      {loading ? (
        <div className="flex justify-center items-center p-10">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : Object.keys(todosByDate).length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">Aucune tâche disponible</h3>
          <p className="text-gray-500 mt-2">Ajoutez une tâche pour commencer à suivre votre progression.</p>
          <button onClick={addTodo} className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md">
            Ajouter une tâche
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(todosByDate).map(([date, dateTodos]) => (
            <div key={date} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-700">{date}</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {dateTodos.map((todo) => (
                  <div
                    key={todo._id}
                    className="px-4 py-3 flex items-center cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setSelectedTodo(todo);
                      setShowModal(true);
                    }}>
                    <div className="flex-shrink-0 mr-3">{getStatusIcon(todo.status)}</div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${todo.status === "completed" ? "text-gray-400 line-through" : "text-gray-900"}`}>{todo.title || "Nouvelle tâche"}</p>
                      {todo.comments?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {todo.comments.length} commentaire{todo.comments.length > 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {todo.lead_user_avatar && (
                        <div className="flex-shrink-0 hidden sm:block">
                          <img src={todo.lead_user_avatar} alt={todo.lead_user_name} className="w-7 h-7 rounded-full" />
                        </div>
                      )}
                      <div className="flex-shrink-0">{getStatusLabel(todo.status)}</div>
                      <div className="flex-shrink-0">
                        <BsThreeDotsVertical className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <TodoModal
        todo={selectedTodo}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedTodo(null);
          loadTodos(); // Refresh the list when closing modal
        }}
        onDelete={deleteTodo}
        updateField={updateField}
        onAddComment={addComment}
      />

      <div className="fixed bottom-6 right-6">
        <button
          onClick={addTodo}
          className="flex items-center justify-center w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
        </button>
      </div>
    </div>
  );
};
